import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { SKIP_ERROR_NOTIFICATION } from '@core/error-handling/services/http-error-notification.interceptor';

export interface AlocacaoAula {
  id?: number;
  numero: number;
  data: string;
  inicioAula: string;
  fimAula: string;
  realizada: boolean;
  idItemEducacional: number;
  descricaoItemEducacional: string;
  disabled?: boolean;
  /** Multiturno: turnos da alocação quando informados pela API. */
  turnos?: string[];
}

export interface DiaAlocacao {
  data: string;
  aulas: AlocacaoAula[];
  realizada?: boolean;
}

export interface FrequenciaAula {
  id?: number;
  enturmacao: {
    id: number;
    nome: string;
    numero: number;
    turma?: any;
  };
  alocacaoAula: {
    id?: number;
    numero: number;
    data: string;
    inicioAula: string;
    fimAula: string;
    itemEducacional: {
      id: number;
      descricao: string;
    };
    turma?: any;
    periodoAvaliativo?: any;
  };
  justificado: boolean;
  abonado: boolean;
  tipo?: 'PRESENCA' | 'FALTA';
}

export interface FrequenciaAulaRequest {
  enturmacao: {
    id: number;
  };
  alocacaoAula: {
    id?: number;
    numero: number;
    data: string;
    inicioAula: string;
    fimAula: string;
    itemEducacional: {
      id: number;
    };
    turma: {
      id: number;
    };
    periodoAvaliativo?: {
      id: number;
    };
  };
}

export interface FrequenciaAulaResponse {
  registroFrequenciaAula: FrequenciaAula;
  registroFaltas: {
    id: number;
    quantidadeFaltas: number;
  };
  resultadoFrequencia: any;
  resultadosPeriodos: any[];
}

export interface EnturmacaoFrequencia {
  id: number;
  turnoFrequenta?: string;
  aluno: {
    id: number;
    nome: string;
    nomeSocial?: string;
    /** Algumas APIs enviam nome social como nome fantasia no cadastro do aluno */
    nomeFantasia?: string;
  };
  numero: number;
  nome: string;
  nomeSocial?: string;
  dataInicio: string;
  dataFim?: string;
  faltasTotais?: any;
  registroFaltas?: {
    id: number;
    quantidadeFaltas: number;
  };
  situacaoItensEducacionais?: any[];
  frequenciasAulas: FrequenciaAulaComputada[];
}

export interface FrequenciaAulaComputada {
  id?: number;
  $$buttonFaltaHidden?: boolean;
  isEnturmado: boolean;
  tipo: 'PRESENCA' | 'FALTA' | null;
  isChecked: boolean;
  disabled: boolean;
  hasDispensa: boolean;
  data: string;
  enturmacao: any;
  justificado: boolean;
  abonado: boolean;
  alocacaoAula: {
    id?: number;
    data: string;
    inicioAula: string;
    fimAula: string;
    numero: number;
    /** Quando a API envia turnos direto na alocação (multiturno). */
    turnos?: string[];
    periodoAvaliativo?: any;
    itemEducacional: {
      id: number;
      descricao: string;
    };
  };
  uniqueId: string;
}

@Injectable({
  providedIn: 'root'
})
export class MobileFrequenciaAulaService {
  private apiUrl = `${environment.apiUrl}/frequencia`;

  constructor(private http: HttpClient) {}

  getAulasDoMes(
    turmaId: number,
    periodoId: number | undefined,
    itemEducacionalIds: number[],
    data: string,
    isAtividade = false,
    isEjaModular = false
  ): Observable<DiaAlocacao[]> {
    let params = new HttpParams()
      .set('data', data)
      .set('isAtividade', String(isAtividade))
      .set('isEjaModular', String(isEjaModular));

    // Turmas de atividade/modular: periodoId pode ser 0/undefined, backend usa isAtividade=true
    if (periodoId) {
      params = params.set('periodoId', String(periodoId));
    }

    itemEducacionalIds.forEach(id => {
      params = params.append('itemEducacional', String(id));
    });

    // ngsw-bypass: campo 'realizada' controla checkbox/ícones e não pode ficar stale.
    const headers = new HttpHeaders({ 'ngsw-bypass': 'true' });

    return this.http.get<DiaAlocacao[]>(
      `${this.apiUrl}/falta-aula/turma/${turmaId}/cabecalho`,
      { params, headers, context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) }
    );
  }

  /** Paginação recursiva até esgotar hasNext. */
  getFrequenciaAula(
    turmaId: number,
    periodoId: number | undefined,
    data: string,
    itemEducacionalIds: number[]
  ): Observable<FrequenciaAula[]> {
    return this.getFrequenciaAulaPage(
      turmaId,
      periodoId,
      data,
      itemEducacionalIds,
      500, // PAGE_LIMIT
      0    // PAGE_OFFSET
    );
  }

  private getFrequenciaAulaPage(
    turmaId: number,
    periodoId: number | undefined,
    data: string,
    itemEducacionalIds: number[],
    limit: number,
    offset: number,
    accumulated: FrequenciaAula[] = []
  ): Observable<FrequenciaAula[]> {
    let params = new HttpParams()
      .set('data', data)
      .set('limit', String(limit))
      .set('offset', String(offset));

    if (periodoId) {
      params = params.set('periodoId', String(periodoId));
    }

    itemEducacionalIds.forEach(id => {
      params = params.append('itemEducacional', String(id));
    });

    // ngsw-bypass: registros de falta/presença mudam a cada interação, não pode ficar stale.
    const headers = new HttpHeaders({ 'ngsw-bypass': 'true' });

    return this.http.get<{ content: FrequenciaAula[]; hasNext: boolean }>(
      `${this.apiUrl}/falta-aula/turma/${turmaId}`,
      { params, headers }
    ).pipe(
      switchMap(result => {
        const allContent = [...accumulated, ...result.content];
        
        if (result.hasNext) {
          return this.getFrequenciaAulaPage(
            turmaId,
            periodoId,
            data,
            itemEducacionalIds,
            limit,
            offset + limit,
            allContent
          );
        }
        
        return of(allContent);
      })
    );
  }

  savePorAula(request: FrequenciaAulaRequest): Observable<FrequenciaAulaResponse> {
    return this.http.post<FrequenciaAulaResponse>(
      `${this.apiUrl}/falta-aula`,
      request
    );
  }

  removeFrequenciaAula(id: number): Observable<FrequenciaAulaResponse> {
    return this.http.delete<FrequenciaAulaResponse>(
      `${this.apiUrl}/falta-aula/${id}`
    );
  }

  /**
   * Marca aulas como realizadas (PUT). A API aplica presença automática a todos os
   * alunos, preservando faltas/dispensas já registradas.
   */
  marcarAulasRealizadas(aulas: any[]): Observable<any[]> {
    return this.http.put<any[]>(
      `${this.apiUrl}/alocacao-aula`,
      aulas
    );
  }

  getEnturmacoesFrequencia(
    turmaId: number,
    itemEducacionalIds: number[],
    isAtividade = false,
    isEjaModular = false
  ): Observable<EnturmacaoFrequencia[]> {
    const filters: string[] = [];

    if (isAtividade) {
      filters.push(`(atividade = ${itemEducacionalIds.join(',')})`);
    }

    if (isEjaModular) {
      filters.push(`(itemEducacional = ${itemEducacionalIds.join(',')})`);
    }

    const filterString = filters.join(' and ');
    let params = new HttpParams();
    
    if (filterString) {
      params = params.set('filter', filterString);
    }

    return this.http.get<EnturmacaoFrequencia[]>(
      `${environment.apiUrl}/frequencia/turma/${turmaId}/frequencias/enturmacoes`,
      { params }
    );
  }

  getPeriodosDispensa(
    turmaId: number,
    enturmacaoId: number,
    itemEducacionalId: number
  ): Observable<any[]> {
    return this.http.get<any[]>(
      `${environment.apiUrl}/frequencia/turma/${turmaId}/frequencias/periodos-dispensa/enturmacao/${enturmacaoId}/item-educacional/${itemEducacionalId}`
    );
  }

  carregarDadosFrequenciaAula(
    turmaId: number,
    periodoId: number | undefined,
    itemEducacionalIds: number[],
    data: string,
    isAtividade = false,
    isEjaModular = false
  ): Observable<{ aulas: DiaAlocacao[]; frequencias: FrequenciaAula[] }> {
    return forkJoin({
      aulas: this.getAulasDoMes(
        turmaId,
        periodoId,
        itemEducacionalIds,
        data,
        isAtividade,
        isEjaModular
      ),
      frequencias: this.getFrequenciaAula(
        turmaId,
        periodoId,
        data,
        itemEducacionalIds
      )
    });
  }
}
